import { Flex, Text } from '@mantine/core';
import { formatCurrency } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import {
  OwnersEditor,
  type OwnerRowValue,
  productSupportsOwners,
} from 'src/financial/financial-data/financial-item/forms/common/OwnersEditor';
import {
  DetectionState,
  EntityCategory,
  selectExistingOwners,
  selectIsDiscarded,
  selectIsResolved,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';

// The detected item's total lives under different field names per product; used only for
// the per-owner money readout.
const VALUE_FIELD_BY_PRODUCT: Record<string, string> = {
  SpendingAccount: 'current_balance',
  Mortgage: 'remaining_balance',
  Loan: 'remaining_balance',
  Protection: 'cover_amount',
};

type Props = {
  category: EntityCategory;
  detection: DetectionState;
};

// Data Upload Review owners editor: lets advisors correct who a detected item belongs to
// before import. Reuses the create form's controlled OwnersEditor and writes edits back to
// the review store, from where they are forwarded on finalise (see getPayloadForNewDetection).
export function DetectionOwnersEditor({ category, detection }: Props) {
  const { flagEnabledFinancialItemOwners } = useFlags();
  const { members } = useFamilyMembers();
  const tenantCurrency = useCurrency();
  const setResolvedOwners = useDetectedEntitiesStore(state => state.setResolvedOwners);
  // Add new, Update and discard all save immediately, so owners edited afterwards would never
  // reach the backend. The detected fields lock on the same state.
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detection.id),
  );
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detection.id),
  );
  const existingOwners = useDetectedEntitiesStore(state =>
    selectExistingOwners(state, category, detection.id),
  );

  if (!flagEnabledFinancialItemOwners || !productSupportsOwners(detection.entityType)) {
    return null;
  }

  const detectedOwners: OwnerRowValue[] = detection.resolvedOwners ?? [];
  const recordOwners = (existingOwners as OwnerRowValue[] | undefined) ?? [];
  // On a conflict the adviser is correcting a record, not the document, so start from what that
  // record actually owns. Showing the detected set instead would let one nudged share replace
  // hand-entered externals they were never shown. A record with no owners of its own has nothing
  // to lose, so the detected set stands as the proposal.
  const unedited = recordOwners.length > 0 ? recordOwners : detectedOwners;
  const owners = detection.ownersEdited ? detectedOwners : unedited;
  const valueField = VALUE_FIELD_BY_PRODUCT[detection.entityType] ?? 'value';
  const valueMetadata = detection.fieldsByKey[valueField]?.metadata;
  const total = Number(detection.fieldsByKey[valueField]?.value) || 0;
  // The detection carries its own currency, so an owner's slice reads in the same currency and
  // style as the value shown above it on the card. Tenant currency covers a non-money field.
  const currency = valueMetadata?.type === 'MONEY' ? valueMetadata.currency : tenantCurrency;

  const handleChange = (next: OwnerRowValue[]) => setResolvedOwners(category, detection.id, next);

  return (
    <Flex direction="column" gap="md" data-testid="detection-owners-editor">
      <Text fw={700} size="sm" c="gray.10">
        Owners
      </Text>
      <OwnersEditor
        owners={owners}
        onChange={handleChange}
        members={members}
        total={total}
        formatAmount={amount => formatCurrency(amount, currency) ?? ''}
        disabled={isResolved || isDiscarded}
      />
    </Flex>
  );
}
